import React, { useState, Suspense, useEffect } from "react";
import ReactDOM from "react-dom";
import { VariantProps } from ".";
import Registered from "./Registered";
import StandardPrice from "../Price";
import Icon from "~/common/Icons/Standard";
import useIsMounted from "~/common/useIsMounted";
import { SelectedProduct } from "~/common/Store/models/ProductListing";

import(
  /* webpackChunkName: "modals-standalone" */ "~/bootstrap/less/modals-standalone.less"
);

const PriceGuaranteeModal = React.lazy(
  () => import("~/common/PriceGuaranteeModalContainer/index"),
);

const L = ({
  user,
  selectedProduct,
  chat,
  productListingView,
  setIsQuantityDiscountPopoverOpen,
}: VariantProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const isMounted = useIsMounted();
  const { statusText } = chat;
  const [currentUrl, setCurrentUrl] = useState("");

  const showRegistered =
    user.registered &&
    user.hasPurchaseHistory &&
    (selectedProduct.showBelowMinimumAdvertisedPriceForLoggedInCustomers ||
      (selectedProduct.showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers &&
        (user.hasWebPlus || (user.platinum && user.platinum.enabled))));

  useEffect(() => {
    setCurrentUrl(window.location.pathname);
  }, []);

  const handleChatLinkClick = (event: React.SyntheticEvent) => {
    event.preventDefault();

    window.open(
      `/chat/?referringURL=${encodeURIComponent(currentUrl)}`,
      "",
      "width=650,height=700,resizable=1,scrollbars=1",
    );
  };

  if (showRegistered) {
    return (
      <Registered
        {...{
          user,
          selectedProduct,
          setIsQuantityDiscountPopoverOpen,
        }}
      />
    );
  }

  return (
    <div className="map map-override">
      <div
        className={`bg-white border-red-500 rounded border-solid border ${
          productListingView === "list"
            ? "my-0 max-w-full w-40 xs:mx-auto xs:text-center"
            : "text-center"
        }`}
      >
        {statusText && statusText === "online" ? (
          <DisplayLowestGuaranteedPriceWithChatNowLink
            selectedProduct={selectedProduct}
            currentUrl={currentUrl}
            handleChatLinkClick={handleChatLinkClick}
          />
        ) : (
          <DisplayLowestGuaranteedPriceWithRequestQuote
            selectedProduct={selectedProduct}
            setModalOpen={setModalOpen}
            productListingView={productListingView}
          />
        )}
        {isMounted && modalOpen && (
          <>
            {ReactDOM.createPortal(
              <Suspense fallback={null}>
                <PriceGuaranteeModal
                  modalOpen={modalOpen}
                  onClose={() => setModalOpen(false)}
                  itemNumber={selectedProduct.itemNumber}
                />
              </Suspense>,

              document.querySelector("body") as Element,
            )}
          </>
        )}
      </div>
    </div>
  );
};

interface DisplayLowestGuaranteedPriceWithRequestQuoteProps {
  selectedProduct: SelectedProduct;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  productListingView?: string;
}

function DisplayLowestGuaranteedPriceWithRequestQuote({
  selectedProduct,
  setModalOpen,
  productListingView,
}: DisplayLowestGuaranteedPriceWithRequestQuoteProps) {
  return (
    <button
      className={`bg-transparent border-0 cursor-pointer focus:outline-black p-1 w-full ${
        productListingView === "list" ? "text-left xs:text-center" : ""
      }`}
      data-item-number={selectedProduct.itemNumber}
      type="button"
      onClick={() => {
        setModalOpen(true);
      }}
    >
      <StandardPrice
        price={selectedProduct.price.price}
        unitOfMeasure={selectedProduct.unitOfMeasure}
        textSize="custom"
        className="text-lg pt-0 xxs:text-2xl"
      />
      <p className="text-xs leading-tight mb-0">
        <span className="font-semibold text-red-500">
          Lowest Price Guarantee
        </span>
        <span className="block text-gray-800">
          Request Quote
          <Icon name="breadcrumb" className="h-3 align-middle w-2" />
        </span>
      </p>
    </button>
  );
}

interface DisplayLowestGuaranteedPriceWithChatNowLinkProps {
  selectedProduct: SelectedProduct;
  currentUrl: string;
  handleChatLinkClick: (e: React.SyntheticEvent) => void;
}

function DisplayLowestGuaranteedPriceWithChatNowLink({
  selectedProduct,
  currentUrl,
  handleChatLinkClick,
}: DisplayLowestGuaranteedPriceWithChatNowLinkProps) {
  return (
    <a
      className="block p-1"
      data-item-number={selectedProduct.itemNumber}
      href={`/chat/?referringURL=${encodeURIComponent(currentUrl)}`}
      onClick={handleChatLinkClick}
      target="_blank"
    >
      <StandardPrice
        price={selectedProduct.price.price}
        unitOfMeasure={selectedProduct.unitOfMeasure}
        textSize="custom"
        className="text-lg pt-0 xxs:text-2xl"
      />
      <p className="text-xs leading-tight mb-0">
        <span className="font-semibold text-red-500">
          Lowest Price Guarantee
        </span>
        <span className="block text-gray-800">
          Chat Now
          <Icon name="breadcrumb" className="h-3 align-middle w-3" />
        </span>
      </p>
    </a>
  );
}

export default L;
